import { SpinningIcon } from 'components/Common/Spinner/Spinning'
import { AddWorkingStepButton } from 'components/ProjectPage/BillOfMaterials/Rows/Components/RowWorkingSteps/WorkingSteps/AddWorkingStepButton'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { useAppController } from 'customHooks/useAppController'
import { PartTypeNestingDirection } from 'features/BillOfMaterials/PartType/PartTypeNestingDirection'
import { AllActivitiesInOrderSelector } from 'features/BillOfMaterials/store/selectors/activities/allBomItemActivitiesSelector'
import { bomItemActivitiesSelectors } from 'features/BillOfMaterials/store/selectors/activities/bomItemActivitiesSelectors'
import { projectSelectors } from 'features/BillOfMaterials/store/selectors/projectSelectors'
import { isEqual } from 'lodash'
import { BomItemType } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { BoMItemActivityDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import Sortable from 'sortablejs'
import { ShowException } from 'store/Application/appActions'
import { useAppSelector } from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import { ActivityWithArticle } from './ActivityWithArticle'
import { ActivityWithoutArticle } from './ActivityWithoutArticle'

type Props = {
  bomItemPointer: BomItemPointer
  /**
   * in the material view we should not show the keywords input, just a button to open the article selector
   */
  keywordsInputVariant?: 'input' | 'button'
  /**
   * will disable the list of working steps below the keywords input
   */
  keywordsOnly?: boolean
  dataTestId?: string
}

const useStyles = makeStyles({ name: 'BomItemActivities' })((theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: '100%',
    justifyContent: 'space-between',
  },
  activityWithArticles: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  activityWithoutArticles: {
    width: '100%',
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
    paddingLeft: theme.spacing(0.5),
    minHeight: '30px',
    flexWrap: 'wrap',
    paddingRight: theme.spacing(18),
  },
  sortableActivity: {
    cursor: 'grab',
  },
  ghost: {
    opacity: 0.4,
  },
}))

function restoreWorkingStepPosition(event: Sortable.SortableEvent) {
  if (event.oldIndex === undefined) {
    return
  }

  const siblingsWithoutMovedItem = Array.from(event.from.children).filter(
    (child) => child !== event.item,
  )
  const referenceElement = siblingsWithoutMovedItem[event.oldIndex] ?? null

  event.from.insertBefore(event.item, referenceElement)
}

export function BomItemActivities(props: Props) {
  const { classes } = useStyles()
  const activityWithoutArticlesRef = useRef<HTMLUListElement>(null)
  const [isDraggingWorkingStep, setIsDraggingWorkingStep] = useState(false)
  const { controller } = useAppController(() => new WorkingStepsController())

  const bomItemActivities: BoMItemActivityDto[] = useAppSelector(
    AllActivitiesInOrderSelector(props.bomItemPointer),
    isEqual,
  )

  const rawMaterialActivity: BoMItemActivityDto = useAppSelector(
    bomItemActivitiesSelectors.rawMaterialActivity(props.bomItemPointer),
    isEqual,
  )

  const activitiesWithArticles = bomItemActivities?.filter(
    (act) =>
      act.supportsArticle &&
      act.primaryWorkingStep !== rawMaterialActivity?.primaryWorkingStep,
  )

  const projectIsEditable = useAppSelector(
    projectSelectors.ProjectEditableStateSelector,
  )
  const isBuyerView = useAppSelector(
    projectSelectors.projectInBuyerViewSelector,
  )
  const canReorderWorkingSteps = projectIsEditable && !isBuyerView

  const onWorkingStepOrderChanged = useCallback(
    async (event: Sortable.SortableEvent) => {
      const movedActivityId = event.item.dataset.workingStepActivityId

      if (
        !movedActivityId ||
        event.oldIndex === undefined ||
        event.newIndex === undefined ||
        event.oldIndex === event.newIndex
      ) {
        return true
      }

      let previousElementSibling = event.item
        .previousElementSibling as HTMLLIElement | null
      let moveAfterActivityId: string | undefined

      while (previousElementSibling) {
        moveAfterActivityId =
          previousElementSibling.dataset.workingStepActivityId

        if (moveAfterActivityId) {
          break
        }

        previousElementSibling =
          previousElementSibling.previousElementSibling as HTMLLIElement | null
      }

      try {
        await controller.BatchUpdateWorkingStepsPosition({
          items: [
            {
              bomItemId: props.bomItemPointer.id,
              bomItemActivityId: movedActivityId,
              moveAfterBomItemActivityId: moveAfterActivityId,
            },
          ],
        })
      } catch (error) {
        ShowException('BatchUpdateWorkingStepsPosition', error)
        return false
      }

      return true
    },
    [controller, props.bomItemPointer],
  )

  useEffect(() => {
    if (!canReorderWorkingSteps || !activityWithoutArticlesRef.current) {
      return undefined
    }

    const sortable = Sortable.create(activityWithoutArticlesRef.current, {
      animation: 150,
      draggable: '[data-working-step-activity-id]',
      fallbackOnBody: true,
      ghostClass: classes.ghost,
      chosenClass: classes.ghost,
      onStart: () => setIsDraggingWorkingStep(true),
      onEnd: async (event) => {
        const wasOrderUpdated = await onWorkingStepOrderChanged(event)

        if (!wasOrderUpdated) {
          restoreWorkingStepPosition(event)
        }

        setIsDraggingWorkingStep(false)
      },
    })

    return () => {
      sortable.destroy()
    }
  }, [canReorderWorkingSteps, classes.ghost, onWorkingStepOrderChanged])

  if (!bomItemActivities && !rawMaterialActivity) {
    return null
  }

  return (
    <div className={classes.root}>
      <Suspense
        fallback={
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SpinningIcon />
          </div>
        }
      >
        <ul className={classes.activityWithArticles}>
          {props.bomItemPointer.type !== BomItemType.assemblyType &&
            props.bomItemPointer.type !== BomItemType.assemblyInstance && (
              <ActivityWithArticle
                bomItemPointer={props.bomItemPointer}
                activity={rawMaterialActivity}
                keywordsInputVariant={props.keywordsInputVariant}
                dataTestId={
                  props.dataTestId
                    ? `${props.dataTestId}-${rawMaterialActivity?.primaryWorkingStep}`
                    : undefined
                }
              />
            )}
          {activitiesWithArticles.map((bomItemActivity) => (
            <li key={bomItemActivity.id}>
              <ActivityWithArticle
                bomItemPointer={props.bomItemPointer}
                activity={bomItemActivity}
                keywordsInputVariant={props.keywordsInputVariant}
                dataTestId={
                  props.dataTestId
                    ? `${props.dataTestId}-${bomItemActivity.primaryWorkingStep}`
                    : undefined
                }
              />
            </li>
          ))}
        </ul>
        <ul
          className={classes.activityWithoutArticles}
          ref={activityWithoutArticlesRef}
        >
          {!props.keywordsOnly &&
            projectIsEditable &&
            props.bomItemPointer.type !== BomItemType.partInstance && (
              <li>
                <AddWorkingStepButton
                  bomItemPointers={[props.bomItemPointer]}
                />
              </li>
            )}
          {/* {props.bomItemPointer.type !== BomItemType.partInstance &&
            bomItemActivities.find(
              (x) => x.primaryWorkingStep === WorkingStepType.SheetCutting
            ) && (
              <li>
                <PartTypeNestingDirection
                  bomItemPointer={props.bomItemPointer}
                  workingStepType={WorkingStepType.SheetCutting}
                />
              </li>
            )} */}
          {!props.keywordsOnly &&
            bomItemActivities
              .filter((x) => {
                // if the project is not editable, show only the activities that do not support articles
                if (projectIsEditable) return true

                return false
                // return x.supportsArticle === false
              })
              .map((bomItemActivity) => (
                <li
                  key={bomItemActivity.id + '_no_article'}
                  className={
                    canReorderWorkingSteps
                      ? classes.sortableActivity
                      : undefined
                  }
                  data-working-step-activity-id={bomItemActivity.id}
                >
                  <ActivityWithoutArticle
                    bomItemPointer={props.bomItemPointer}
                    activity={bomItemActivity}
                    forceCloseTooltip={isDraggingWorkingStep}
                    dataTestId={
                      props.dataTestId
                        ? `${props.dataTestId}-${bomItemActivity.primaryWorkingStep}`
                        : undefined
                    }
                  />
                </li>
              ))}
          <li
            style={{
              marginLeft: 'auto',
              paddingRight: '2rem',
              transform: 'translateY(-2px)',
            }}
          >
            <PartTypeNestingDirection bomItemPointer={props.bomItemPointer} />
          </li>
        </ul>
      </Suspense>
    </div>
  )
}
